/**
 * ProgramsPage.js — /programs
 *
 * Aktif programlarin (Kabin, Insan Kaynaklari, Psikoloji, Dil Egitimi) grid'i.
 * Her kart kendi rengiyle, "Programa Git" butonuyla.
 */
import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Plane, Users, Brain, Languages, Briefcase, Book, GraduationCap, Heart, Code, Music, WifiOff, Layers } from 'lucide-react';
import { motion, useReducedMotion } from 'framer-motion';
import { usePrograms } from '@/hooks/usePrograms';
import { useLanguage } from '@/contexts/LanguageContext';
import Navbar from '@/components/Navbar';
import { staggerContainer, cardHover, fadeUp, maybe } from '@/lib/motionVariants';

// Lucide ikon eslemesi (backend "icon" alanindaki string -> bilesen)
const ICON_MAP = {
  Plane, Users, Brain, Languages, Briefcase, Book, GraduationCap, Heart, Code, Music,
};

function IconFor({ name, color, size = 32 }) {
  const Comp = ICON_MAP[name] || Book;
  return <Comp size={size} color={color} strokeWidth={2.2} aria-hidden="true" />;
}

export default function ProgramsPage() {
  const { data: programs = [], isLoading, isError, refetch } = usePrograms();
  const { t } = useLanguage();
  const reduced = useReducedMotion();

  useEffect(() => {
    document.body.classList.add('duo-page');
    return () => document.body.classList.remove('duo-page');
  }, []);

  return (
    <div className="min-h-screen bg-white" data-testid="programs-page">
      <Navbar />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <header className="mb-10 text-center">
          <h1
            className="text-3xl sm:text-4xl font-duo-display text-[var(--text-main)]"
            data-testid="programs-title"
          >
            {t('programs.pageTitle')}
          </h1>
          <p className="mt-3 text-[var(--text-muted)] max-w-2xl mx-auto">
            {t('programs.pageTitle.sub')}
          </p>
        </header>

        {/* Loading skeleton */}
        {isLoading && (
          <div
            className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6"
            aria-label={t('programs.loading.ariaLabel')}
            data-testid="programs-loading"
          >
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="card-duo animate-pulse h-52" />
            ))}
          </div>
        )}

        {/* Error state */}
        {!isLoading && isError && (
          <div className="card-duo p-12 text-center" data-testid="programs-error">
            <WifiOff size={40} className="mx-auto text-[var(--text-muted)] mb-4" aria-hidden="true" />
            <p className="font-duo-display text-lg text-[var(--text-main)] mb-1">
              {t('programs.error.title')}
            </p>
            <p className="text-sm text-[var(--text-muted)] mb-4">{t('programs.error.sub')}</p>
            <button onClick={() => refetch()} className="btn-duo-blue">
              {t('common.retry')}
            </button>
          </div>
        )}

        {/* Empty state */}
        {!isLoading && !isError && programs.length === 0 && (
          <div className="card-duo p-12 text-center" data-testid="programs-empty">
            <Layers size={40} className="mx-auto text-[var(--text-muted)] mb-4" aria-hidden="true" />
            <p className="font-duo-display text-lg text-[var(--text-main)] mb-1">
              {t('programs.empty.title')}
            </p>
            <p className="text-sm text-[var(--text-muted)] mb-4">{t('programs.empty.sub')}</p>
            <Link to="/courses" className="btn-duo-primary">
              {t('programs.empty.cta')}
            </Link>
          </div>
        )}

        {/* Program grid */}
        {!isLoading && !isError && programs.length > 0 && (
          <motion.div
            className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6"
            variants={maybe(reduced, staggerContainer(0.07, 0.05))}
            initial="hidden"
            animate="visible"
          >
            {programs.map((p) => (
              <motion.div
                key={p.slug}
                variants={maybe(reduced, fadeUp)}
                whileHover={reduced ? undefined : cardHover.hover}
                whileTap={reduced ? undefined : cardHover.tap}
                data-testid={`program-card-${p.slug}`}
              >
                <Link
                  to={`/programs/${p.slug}`}
                  className="card-duo p-6 h-full flex flex-col block"
                >
                  <div className="flex items-center justify-between mb-5">
                    <div
                      className="w-14 h-14 rounded-2xl flex items-center justify-center"
                      style={{ backgroundColor: p.color_accent }}
                    >
                      <IconFor name={p.icon} color={p.color_primary} size={28} />
                    </div>
                  </div>
                  <h3
                    className="text-xl font-duo-display mb-2"
                    style={{ color: p.color_primary }}
                    data-testid={`program-name-${p.slug}`}
                  >
                    {p.name}
                  </h3>
                  <p className="text-sm text-[var(--text-muted)] line-clamp-3 flex-1">{p.description}</p>
                  <div className="mt-5 pt-5 border-t border-[var(--duo-stroke)] flex items-center justify-between text-xs">
                    <span className="text-[var(--text-muted)]">{t('programs.card.cta')}</span>
                    <span className="font-bold" style={{ color: p.color_primary }}>
                      →
                    </span>
                  </div>
                </Link>
              </motion.div>
            ))}
          </motion.div>
        )}
      </div>
    </div>
  );
}
